import { CSSProperties, KeyboardEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import useAnimatedMount, { AnimatedMountStatus } from '../hooks/useAnimatedMount';
import useIsMobile from '../hooks/useIsMobile';
import useSystemBarsOverlay from '../hooks/useSystemBarsOverlay';
import { acquireScrollLock, releaseScrollLock } from '../utils/scrollLock';

import styles from './BaseModal.module.scss';

const DEFAULT_MODAL_WIDTH = 466;
const MODAL_HORIZONTAL_OFFSET = 8;
const MODAL_Z_INDEX = 2100;
const MODAL_ANIMATION_DURATION_MS = 350;
const BACKDROP_COLOR = 'rgba(27, 43, 77, 0.5)';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export type BaseModalProps = {
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
  customStyle?: CSSProperties;
  isDiscover?: boolean;
  noPadding?: boolean;
  toggle?: () => void;
  width?: number;
  contentClassName?: string;
};

const BaseModal = (props: BaseModalProps): JSX.Element | null => {
  const {
    customStyle,
    isOpen,
    children,
    className,
    toggle,
    isDiscover,
    noPadding,
    width = DEFAULT_MODAL_WIDTH,
    contentClassName,
  } = props;

  const { isMobile } = useIsMobile();
  const [closedInternally, setClosedInternally] = useState(false);
  const toggleRef = useRef(toggle);
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const mouseDownOnBackdropRef = useRef(false);
  const wasMountedRef = useRef(false);

  useEffect(() => {
    toggleRef.current = toggle;
  }, [toggle]);

  useEffect(() => {
    if (isOpen) setClosedInternally(false);
  }, [isOpen]);

  const effectiveIsOpen = isOpen && !closedInternally;
  const { isMounted, status } = useAnimatedMount(effectiveIsOpen, MODAL_ANIMATION_DURATION_MS);

  useSystemBarsOverlay(effectiveIsOpen, { statusBar: BACKDROP_COLOR });

  const handleToggle = () => {
    setClosedInternally(true);
  };

  useEffect(() => {
    if (!effectiveIsOpen) return undefined;

    acquireScrollLock();

    return () => releaseScrollLock();
  }, [effectiveIsOpen]);

  // Fire the consumer's `toggle` once the close animation has fully completed
  useEffect(() => {
    if (wasMountedRef.current && !isMounted) {
      toggleRef.current?.();
    }
    wasMountedRef.current = isMounted;
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted || typeof document === 'undefined') return undefined;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();

    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isMounted]);

  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return null;
  }

  const isClosing = status === AnimatedMountStatus.Closing;

  const modalWidthBasicStyles: CSSProperties = {
    maxWidth: width,
    margin: 'auto',
    position: 'absolute',
    left: MODAL_HORIZONTAL_OFFSET,
    right: MODAL_HORIZONTAL_OFFSET,
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      handleToggle();
      return;
    }

    if (event.key !== 'Tab' || !modalRef.current) return;

    const focusable = Array.from(modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleBackdropMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    mouseDownOnBackdropRef.current = event.target === event.currentTarget;
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (event.target === event.currentTarget && mouseDownOnBackdropRef.current) {
      handleToggle();
    }
    mouseDownOnBackdropRef.current = false;
  };

  const modalNode = (
    <>
      <div
        className={classNames(
          styles.backdrop,
          isClosing ? styles.backdropClosing : styles.backdropOpen,
        )}
        style={{ zIndex: MODAL_Z_INDEX }}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={modalRef}
        className={classNames(styles.modal, className)}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ display: 'block', zIndex: MODAL_Z_INDEX }}
        onKeyDown={handleKeyDown}
        onMouseDown={handleBackdropMouseDown}
        onClick={handleBackdropClick}
      >
        <div
          className={classNames(
            styles.dialog,
            isClosing ? styles.dialogClosing : styles.dialogOpen,
          )}
          role="document"
          style={customStyle && Object.keys(customStyle).length > 0 ? customStyle : modalWidthBasicStyles}
        >
          <div
            className={classNames(
              styles.content,
              isDiscover ? styles.modalContentDiscover : styles.modalContent,
              contentClassName,
              {
                [styles.mobile]: isMobile,
                [styles.web]: !isMobile,
                [styles.noPadding]: noPadding,
              },
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalNode, document.body);
};

export default BaseModal;
